import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR, formatDate, todayISO, daysBetween } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Receipt, CircleDollarSign, Printer, AlertCircle, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATUS_TONE = {
  unpaid: "bg-warning/15 text-warning",
  partial: "bg-info/15 text-info",
  paid: "bg-success/15 text-success",
};

const STATUS_LABEL = { unpaid: "Belum Bayar", partial: "Cicilan", paid: "Lunas" };

export default function Bills() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = {
    status: params.get("status") || "",
    unit_id: params.get("unit_id") || "",
    tenant_id: params.get("tenant_id") || "",
  };
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => v && qs.append(k, v));

  const { data: bills = [], isLoading } = useSWR(`/bills?${qs.toString()}`, fetcher);
  const { data: units = [] } = useSWR("/units", fetcher);
  const { data: tenants = [] } = useSWR("/tenants", fetcher);
  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);

  const [payFor, setPayFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const overdueCount = useMemo(
    () => bills.filter((b) => b.status !== "paid" && b.due_date < todayISO()).length,
    [bills]
  );

  const runBulk = async () => {
    setBulkRunning(true);
    try {
      const { data } = await api.post("/reminders/bulk/overdue");
      setBulkResult(data);
      toast.success(`WA terkirim: ${data.sent} · Gagal: ${data.failed} · Tanpa nomor: ${data.no_number}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal kirim massal");
    } finally {
      setBulkRunning(false);
    }
  };

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  return (
    <div className="space-y-6" data-testid="bills-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Operasional
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Tagihan</h1>
          <p className="text-sm text-muted-foreground mt-1">Catat pembayaran, kelola cicilan, dan cetak kwitansi.</p>
        </div>
        {overdueCount > 0 && (
          <Button
            onClick={() => { setBulkResult(null); setBulkOpen(true); }}
            variant="destructive"
            className="gap-2 shrink-0"
            data-testid="bulk-wa-overdue-button"
          >
            <MessageCircle className="h-4 w-4" /> Kirim WA ke {overdueCount} Overdue
          </Button>
        )}
      </header>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Status</Label>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="unpaid">Belum Bayar</SelectItem>
                <SelectItem value="partial">Cicilan</SelectItem>
                <SelectItem value="paid">Lunas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Unit</Label>
            <Select value={filters.unit_id || "all"} onValueChange={(v) => setFilter("unit_id", v)}>
              <SelectTrigger><SelectValue placeholder="Semua unit" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua unit</SelectItem>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.code} — {u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Penyewa</Label>
            <Select value={filters.tenant_id || "all"} onValueChange={(v) => setFilter("tenant_id", v)}>
              <SelectTrigger><SelectValue placeholder="Semua penyewa" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua penyewa</SelectItem>
                {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : bills.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <Receipt className="h-7 w-7" />
            </div>
            <div>
              <div className="font-display text-lg font-medium">Belum ada tagihan</div>
              <p className="text-sm text-muted-foreground mt-1">Tagihan akan otomatis dibuat saat kontrak dibuat.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bills.map((b) => {
            const overdue = b.status !== "paid" && b.due_date < todayISO();
            const outstanding = Number(b.amount) + Number(b.late_fee || 0) - Number(b.paid_amount || 0);
            return (
              <Card key={b.id} className={overdue ? "border-destructive/40" : ""} data-testid={`bill-row-${b.id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="rounded-md font-mono">{unitMap[b.unit_id]?.code || "?"}</Badge>
                        <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${STATUS_TONE[b.status]}`}>
                          {STATUS_LABEL[b.status]}
                        </Badge>
                        {overdue && (
                          <Badge variant="destructive" className="rounded-full text-[10px] uppercase tracking-wider gap-1">
                            <AlertCircle className="h-3 w-3" /> Lewat {daysBetween(b.due_date, todayISO())} hari
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 font-medium">{tenantMap[b.tenant_id]?.name || "—"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Periode {formatDate(b.period_start)} – {formatDate(b.period_end)} · Jatuh tempo {formatDate(b.due_date)}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:flex sm:items-center gap-4 sm:gap-6 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Tagihan</div>
                        <div className="font-medium tabular-nums">{formatIDR(b.amount)}</div>
                      </div>
                      {b.late_fee > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Denda</div>
                          <div className="font-medium tabular-nums text-destructive">{formatIDR(b.late_fee)}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sisa</div>
                        <div className="font-medium tabular-nums text-primary">{formatIDR(outstanding)}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/bills/${b.id}`)} data-testid={`view-bill-${b.id}`}>
                        Detail
                      </Button>
                      {b.status !== "paid" && (
                        <>
                          <WaReminderButton bill={b} />
                          <Button size="sm" className="gap-1.5" onClick={() => setPayFor(b)} data-testid={`pay-bill-${b.id}`}>
                            <CircleDollarSign className="h-4 w-4" /> Bayar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {payFor && (
        <PaymentDialog
          bill={payFor}
          onClose={() => setPayFor(null)}
          onPaid={(payment) => {
            setPayFor(null);
            mutate((k) => typeof k === "string" && k.startsWith("/bills"));
            mutate("/dashboard/stats");
            toast.success(`Pembayaran dicatat · ${payment.receipt_number}`);
            navigate(`/receipt/${payment.id}`);
          }}
        />
      )}

      <Dialog open={bulkOpen} onOpenChange={(v) => !v && (setBulkOpen(false), setBulkResult(null))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kirim WA ke Semua Overdue</DialogTitle>
            <DialogDescription>
              {bulkResult
                ? "Hasil pengiriman:"
                : `Akan mengirim reminder jatuh tempo ke ${overdueCount} tagihan yang sudah melewati jatuh tempo. Pesan dikirim satu-per-satu untuk menghindari throttling Fonnte.`}
            </DialogDescription>
          </DialogHeader>
          {bulkResult ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-success/10 text-success px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider">Terkirim</div>
                  <div className="font-display text-2xl font-semibold tabular-nums">{bulkResult.sent}</div>
                </div>
                <div className="rounded-md bg-destructive/10 text-destructive px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider">Gagal</div>
                  <div className="font-display text-2xl font-semibold tabular-nums">{bulkResult.failed}</div>
                </div>
                <div className="rounded-md bg-warning/10 text-warning px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider">No. Kosong</div>
                  <div className="font-display text-2xl font-semibold tabular-nums">{bulkResult.no_number}</div>
                </div>
              </div>
              {bulkResult.details && bulkResult.details.some((d) => !d.ok) && (
                <div className="max-h-48 overflow-y-auto text-xs border rounded-md p-2 space-y-1 bg-muted/30">
                  <div className="font-semibold mb-1">Detail gagal:</div>
                  {bulkResult.details.filter((d) => !d.ok).map((d, i) => (
                    <div key={i} className="text-destructive">• {d.tenant || d.bill_id}: {d.error}</div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Cek log lengkap di menu <span className="font-medium text-foreground">Log WhatsApp</span> setelah selesai.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkResult(null); }}>
              {bulkResult ? "Tutup" : "Batal"}
            </Button>
            {!bulkResult && (
              <Button onClick={runBulk} disabled={bulkRunning} className="gap-2" data-testid="confirm-bulk-wa">
                {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {bulkRunning ? "Mengirim..." : "Kirim Sekarang"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WaReminderButton({ bill }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const overdue = bill.due_date <= todayISO();

  const send = async (kind) => {
    setBusy(true);
    try {
      const path = kind === "due" ? `/reminders/bill/${bill.id}/due` : `/reminders/bill/${bill.id}`;
      const { data } = await api.post(path);
      toast.success(`WhatsApp terkirim ke ${data.target}`);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal kirim WhatsApp");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen(true)}
        data-testid={`wa-reminder-${bill.id}`}
      >
        <MessageCircle className="h-4 w-4" /> WA
      </Button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Kirim Reminder via WhatsApp</DialogTitle>
          <DialogDescription>Pesan akan dikirim ke nomor WhatsApp penyewa melalui Fonnte.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-border/60 p-3 space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Jatuh tempo</span><span>{formatDate(bill.due_date)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Nominal</span><span className="font-medium">{formatIDR(Number(bill.amount) + Number(bill.late_fee||0) - Number(bill.paid_amount||0))}</span></div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Button disabled={busy} onClick={() => send("h7")} className="gap-2" data-testid="wa-send-h7">
              <Send className="h-4 w-4" /> Kirim Reminder H-7 (tagihan akan jatuh tempo)
            </Button>
            <Button
              disabled={busy}
              onClick={() => send("due")}
              variant={overdue ? "destructive" : "secondary"}
              className="gap-2"
              data-testid="wa-send-due"
            >
              <Send className="h-4 w-4" /> Kirim Reminder Jatuh Tempo (hari-H / overdue)
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ bill, onClose, onPaid }) {
  const outstanding = Number(bill.amount) + Number(bill.late_fee || 0) - Number(bill.paid_amount || 0);
  const [form, setForm] = useState({
    amount: outstanding,
    method: "cash",
    paid_at: todayISO(),
    note: "",
    include_late_fee: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/payments", {
        bill_id: bill.id,
        amount: Number(form.amount),
        method: form.method,
        paid_at: form.paid_at,
        note: form.note,
        include_late_fee: form.include_late_fee,
      });
      onPaid(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Catat Pembayaran</DialogTitle>
          <DialogDescription>Dukungan pembayaran bertahap (cicilan). Kwitansi otomatis dibuat setelah pembayaran.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-md border border-border/60 p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Tagihan</span><span>{formatIDR(bill.amount)}</span></div>
            {bill.late_fee > 0 && <div className="flex justify-between text-destructive"><span>Denda</span><span>{formatIDR(bill.late_fee)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Sudah dibayar</span><span>{formatIDR(bill.paid_amount || 0)}</span></div>
            <div className="flex justify-between font-medium border-t pt-1"><span>Sisa</span><span>{formatIDR(outstanding)}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Nominal</Label>
              <Input type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Metode</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal Bayar</Label>
            <Input type="date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} />
          </div>
          {bill.late_fee > 0 && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.include_late_fee} onCheckedChange={(v) => setForm({ ...form, include_late_fee: !!v })} />
              Termasuk denda ({formatIDR(bill.late_fee)})
            </label>
          )}
          <div className="space-y-1.5">
            <Label>Catatan</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving} className="gap-1.5" data-testid="payment-submit-button">
              <Printer className="h-4 w-4" />
              {saving ? "Memproses..." : "Simpan & Cetak Kwitansi"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
